import { Archive, ArchiveRestore, Check, FileDown, Link, Paperclip, X } from "lucide-react";
import {
  useState,
  useRef,
  useEffect,
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

import { useDictation } from "@/hooks/useDictation";
import { useSession, useCreateJob, useSetSessionHidden } from "@/hooks/useSession";
import { useSuggestions } from "@/hooks/useSuggestions";
import { uploadAttachments } from "@/lib/api";
import { formatAttachmentSize, mergeAttachmentFiles } from "@/lib/attachments";
import { sessionToMarkdown } from "@/lib/session-to-markdown";
import { useStore } from "@/lib/store";

import { ConversationMessage } from "./ConversationMessage";
import { DictationButton } from "./DictationButton";
import { DictationStatus } from "./DictationStatus";
import { SendButton } from "./SendButton";
import { formatTokens, getModelLabel } from "./stats/formatters";

interface AgentBadgeProps {
  agentId: string;
}

function AgentBadge({ agentId }: AgentBadgeProps): JSX.Element {
  const isPlan = agentId === "plan";
  const colorClasses = isPlan
    ? "bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/30"
    : "bg-cyber-magenta/10 text-cyber-magenta border-cyber-magenta/30";

  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${colorClasses}`}>
      {agentId.toUpperCase()}
    </span>
  );
}

function autoResizeTextarea(textarea: HTMLTextAreaElement): void {
  textarea.style.height = "auto";
  textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
}

export function ConversationView(): JSX.Element | null {
  const activeSessionKey = useStore((state) => state.activeSessionKey);
  const clearSession = useStore((state) => state.clearSession);
  const showToast = useStore((state) => state.showToast);

  const { data, isLoading } = useSession(activeSessionKey);
  const createJob = useCreateJob();
  const setSessionHidden = useSetSessionHidden(activeSessionKey);
  const { data: suggestionData } = useSuggestions(4);
  const suggestions = suggestionData?.suggestions ?? [];

  const [replyMessage, setReplyMessage] = useState("");
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [isUploadingReply, setIsUploadingReply] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);
  const conversationRef = useRef<HTMLDivElement>(null);
  const replyInputRef = useRef<HTMLTextAreaElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const dictation = useDictation({
    onTranscript: (text) => {
      insertReplyTranscriptAtCursor(text);
    },
    onError: (errorMessage) => {
      showToast(errorMessage, "error");
    },
  });

  const isReplyRecording = dictation.status === "recording";
  const isReplyProcessingAudio =
    dictation.status === "transcribing" || dictation.status === "processing";
  const isReplySubmitting = createJob.isPending || isUploadingReply;

  async function toggleSessionHidden(): Promise<void> {
    if (!data) return;
    try {
      await setSessionHidden.mutateAsync(!data.hidden);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to update conversation", "error");
    }
  }

  // Check if any active job in the session has a pending question — if so,
  // the composer should be disabled because the session is busy waiting for
  // the user to answer via the QuestionPrompt UI. Cancelled/failed/completed
  // jobs with stale pendingQuestion metadata should not block the composer.
  const hasPendingQuestion = Boolean(
    data?.jobs.some((j) => {
      if (j.status === "cancelled" || j.status === "failed" || j.status === "completed") {
        return false;
      }
      const meta = j.metadata as { pendingQuestion?: unknown } | null;
      return meta?.pendingQuestion != null;
    }),
  );

  const isReplyBusy = isReplySubmitting || isReplyProcessingAudio || hasPendingQuestion;

  async function copyLink(): Promise<void> {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function copyAsMarkdown(): Promise<void> {
    if (!data) return;
    await navigator.clipboard.writeText(sessionToMarkdown(data));
    setCopiedMarkdown(true);
    showToast("Markdown copied", "success");
    setTimeout(() => setCopiedMarkdown(false), 2000);
  }

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (conversationRef.current) {
      conversationRef.current.scrollTop = conversationRef.current.scrollHeight;
    }
  }, [data?.jobs]);

  useEffect(() => {
    if (isReplyBusy) return;
    const id = window.setTimeout(() => {
      replyInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [activeSessionKey, isReplyBusy]);

  useEffect(() => {
    const textarea = replyInputRef.current;
    if (!textarea) return;
    autoResizeTextarea(textarea);
  }, [replyMessage]);

  if (!activeSessionKey) return null;

  async function handleReply(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!replyMessage.trim() || isReplyBusy || isReplyRecording) return;

    try {
      let attachmentIds: string[] | undefined;
      if (replyFiles.length > 0) {
        setIsUploadingReply(true);
        const uploadResponse = await uploadAttachments(replyFiles);
        attachmentIds = uploadResponse.attachments.map((attachment) => attachment.id);
      }

      await createJob.mutateAsync({
        message: replyMessage.trim(),
        threadKey: activeSessionKey ?? undefined,
        attachmentIds,
      });
      setReplyMessage("");
      setReplyFiles([]);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to send reply";
      showToast(errorMessage, "error");
    } finally {
      setIsUploadingReply(false);
    }
  }

  function insertReplyTranscriptAtCursor(transcript: string): void {
    const normalizedTranscript = transcript.trim();
    if (!normalizedTranscript) {
      return;
    }

    const textarea = replyInputRef.current;
    if (!textarea) {
      setReplyMessage((current) =>
        current.trim().length > 0 ? `${current} ${normalizedTranscript}` : normalizedTranscript,
      );
      return;
    }

    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? textarea.value.length;

    setReplyMessage((current) => {
      const start = Math.min(selectionStart, current.length);
      const end = Math.min(selectionEnd, current.length);
      const before = current.slice(0, start);
      const after = current.slice(end);
      const prefix = before.length > 0 && !/\s$/.test(before) ? " " : "";
      const suffix = after.length > 0 && !/^\s/.test(after) ? " " : "";
      const insertion = `${prefix}${normalizedTranscript}${suffix}`;
      const nextMessage = `${before}${insertion}${after}`;
      const nextCursor = before.length + insertion.length;

      requestAnimationFrame(() => {
        const target = replyInputRef.current;
        if (!target) return;
        target.focus();
        target.selectionStart = nextCursor;
        target.selectionEnd = nextCursor;
        autoResizeTextarea(target);
      });

      return nextMessage;
    });
  }

  function focusReplyInput(): void {
    replyInputRef.current?.focus();
  }

  function handleReplyContainerPointerDown(e: PointerEvent<HTMLDivElement>): void {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest("input,textarea,button,a,select")) return;
    e.preventDefault();
    focusReplyInput();
  }

  function addReplyFiles(incomingFiles: File[]): void {
    const { files, error } = mergeAttachmentFiles(replyFiles, incomingFiles);
    if (error) {
      showToast(error, "error");
      return;
    }
    setReplyFiles(files);
  }

  function handleReplyFileChange(e: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      addReplyFiles(files);
    }
    e.target.value = "";
  }

  function handleReplyPaste(e: ClipboardEvent<HTMLTextAreaElement>): void {
    const files = Array.from(e.clipboardData.files || []);
    if (files.length === 0) {
      return;
    }

    e.preventDefault();
    addReplyFiles(files);
  }

  function handleReplyDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(true);
  }

  function handleReplyDragLeave(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
      return;
    }
    setIsDraggingFiles(false);
  }

  function handleReplyDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setIsDraggingFiles(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      addReplyFiles(files);
    }
  }

  function removeReplyFile(index: number): void {
    setReplyFiles((current) => current.filter((_, currentIndex) => currentIndex !== index));
  }

  function handleReplyKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key !== "Enter") {
      return;
    }

    if (e.ctrlKey) {
      e.preventDefault();
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const nextMessage = `${replyMessage.slice(0, start)}\n${replyMessage.slice(end)}`;
      setReplyMessage(nextMessage);
      requestAnimationFrame(() => {
        textarea.selectionStart = start + 1;
        textarea.selectionEnd = start + 1;
        autoResizeTextarea(textarea);
      });
      return;
    }

    if (e.shiftKey) {
      return;
    }

    e.preventDefault();
    if (replyMessage.trim() && !isReplyBusy && !isReplyRecording) {
      void handleReply(e as unknown as FormEvent);
    }
  }

  async function handleSuggestion(message: string): Promise<void> {
    if (isReplyBusy || isReplyRecording) return;
    await createJob.mutateAsync({
      message,
      threadKey: activeSessionKey ?? undefined,
    });
  }

  const firstMessage = data?.jobs?.[0]?.messageText;
  const fallbackTitle = firstMessage ? `${firstMessage.slice(0, 50)}...` : "Session";
  const rawTitle = data?.sessionTitle ?? fallbackTitle;
  const title = rawTitle.replace(/^mako\s*[–-]\s*/i, "").trim();
  const sessionUsage = data?.usage;

  useEffect(() => {
    document.title = title;
    return () => {
      document.title = "Mako";
    };
  }, [title]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-cyber-border px-2 py-2 md:px-4 md:py-3">
        <div className="flex items-start justify-between gap-2 px-2 md:gap-3 md:px-6">
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-medium text-cyber-text">{title}</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="text-[10px] uppercase text-cyber-muted">
                {data?.source ?? "..."} · {data?.jobs.length ?? 0} message
                {(data?.jobs.length ?? 0) !== 1 ? "s" : ""}
              </span>
              {data && data.origin !== "interactive" && (
                <span className="rounded border border-cyber-yellow/30 bg-cyber-yellow/10 px-1.5 py-0.5 text-[10px] font-medium text-cyber-yellow">
                  {data.origin === "watch" ? "WATCH" : "AUTOMATED"}
                </span>
              )}
              {data?.agentId && <AgentBadge agentId={data.agentId} />}
              {data?.modelId && (
                <span className="font-mono text-[10px] text-cyber-muted">
                  {data.modelId.split("/").pop()}
                </span>
              )}
              {sessionUsage && sessionUsage.responses > 0 && (
                <>
                  <span className="text-[10px] uppercase tracking-wide text-cyber-muted/80">
                    {formatTokens(sessionUsage.totalTokens)} tokens ·{" "}
                    <span className="text-cyber-cyan">
                      {formatTokens(sessionUsage.outputTokens)} output
                    </span>{" "}
                    · {sessionUsage.responses} response
                    {sessionUsage.responses === 1 ? "" : "s"}
                  </span>
                  {sessionUsage.byModel.slice(0, 3).map((row) => (
                    <span
                      key={`${row.providerId}/${row.modelId}`}
                      className="rounded border border-cyber-border bg-cyber-surface/70 px-1.5 py-0.5 text-[10px] text-cyber-muted"
                      title={`${row.providerId}/${row.modelId} — ${formatTokens(row.totalTokens)} total, ${formatTokens(row.outputTokens)} output`}
                    >
                      {getModelLabel(row.providerId, row.modelId, row.modelName)} ·{" "}
                      {formatTokens(row.outputTokens)}
                    </span>
                  ))}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={copyAsMarkdown}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan md:p-2"
              title="Copy as Markdown"
              aria-label="Copy as Markdown"
            >
              {copiedMarkdown ? <Check size={16} /> : <FileDown size={16} />}
            </button>
            <button
              onClick={copyLink}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan md:p-2"
              title="Copy link"
              aria-label="Copy link"
            >
              {copied ? "✓" : <Link size={16} />}
            </button>
            <button
              type="button"
              onClick={toggleSessionHidden}
              disabled={!data || setSessionHidden.isPending}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan disabled:cursor-not-allowed disabled:opacity-50 md:p-2"
              title={data?.hidden ? "Show in sidebar" : "Hide from sidebar"}
              aria-label={data?.hidden ? "Show in sidebar" : "Hide from sidebar"}
            >
              {data?.hidden ? <ArchiveRestore size={16} /> : <Archive size={16} />}
            </button>
            <button
              onClick={clearSession}
              className="rounded p-1.5 text-cyber-muted transition-all hover:bg-cyber-cyan/10 hover:text-cyber-cyan md:p-2"
              title="Close"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div ref={conversationRef} className="flex-1 overflow-y-auto overscroll-contain p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {isLoading ? (
            <div className="py-8 text-center text-cyber-muted">
              <span className="animate-spin-glow mr-2 inline-block">◐</span>
              Loading conversation...
            </div>
          ) : data?.jobs.length ? (
            data?.jobs.map((job) => <ConversationMessage key={job.id} job={job} />)
          ) : (
            <div className="py-8 text-center text-cyber-muted">
              <div className="mb-3 text-xs uppercase tracking-widest text-cyber-muted/70">
                Try one of these
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => handleSuggestion(s.message)}
                    className="rounded-full border border-cyber-border bg-cyber-surface/60 px-3 py-1.5 text-xs text-cyber-text transition-all hover:border-cyber-cyan/60 hover:bg-cyber-cyan/10 hover:text-cyber-cyan"
                    disabled={isReplyBusy || isReplyRecording}
                    title={s.message}
                  >
                    {s.message}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reply form */}
      <div className="flex-shrink-0 p-4 pb-6">
        <form onSubmit={handleReply} className="mx-auto max-w-3xl">
          <input
            ref={replyFileInputRef}
            type="file"
            className="hidden"
            multiple
            onChange={handleReplyFileChange}
            disabled={isReplyBusy}
          />

          <div
            className={`focus-ring min-h-[60px] cursor-text rounded-2xl border border-cyber-border bg-cyber-surface px-4 py-3 ${
              isDraggingFiles ? "border-cyber-magenta/60 bg-cyber-magenta/5" : ""
            }`}
            onPointerDown={handleReplyContainerPointerDown}
            onDragOver={handleReplyDragOver}
            onDragLeave={handleReplyDragLeave}
            onDrop={handleReplyDrop}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg text-cyber-magenta">{">"}</span>
              <textarea
                ref={replyInputRef}
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                onInput={(e) => autoResizeTextarea(e.currentTarget)}
                onPaste={handleReplyPaste}
                onKeyDown={handleReplyKeyDown}
                placeholder="Reply..."
                required
                rows={1}
                className="flex-1 cursor-text resize-none overflow-y-auto rounded-none border-none bg-transparent text-sm text-cyber-text placeholder-cyber-muted/50 focus:shadow-none focus:outline-none focus:ring-0"
                autoComplete="off"
                disabled={isReplyBusy}
              />
              <button
                type="button"
                onClick={() => replyFileInputRef.current?.click()}
                disabled={isReplyBusy || isReplyRecording}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-cyber-border text-cyber-muted transition-all hover:border-cyber-magenta/50 hover:text-cyber-magenta"
                title="Attach files"
                aria-label="Attach files"
              >
                <Paperclip size={16} />
              </button>
              <DictationButton
                variant="magenta"
                status={dictation.status}
                isSupported={dictation.isSupported}
                disabled={isReplyBusy}
                onClick={() => {
                  void dictation.toggleRecording();
                }}
                onPointerDown={(e) => {
                  e.preventDefault();
                  focusReplyInput();
                }}
              />
              <SendButton
                variant="magenta"
                disabled={isReplyBusy || isReplyRecording || !replyMessage.trim()}
                pending={isReplySubmitting}
                onPointerDown={(e) => {
                  e.preventDefault();
                  focusReplyInput();
                }}
              />
            </div>

            <DictationStatus
              variant="magenta"
              status={dictation.status}
              elapsedMs={dictation.elapsedMs}
              level={dictation.level}
            />

            {!dictation.isSupported && (
              <div className="mt-2 text-[11px] text-cyber-muted">
                Voice dictation is unavailable in this browser.
              </div>
            )}

            {(replyFiles.length > 0 || isDraggingFiles) && (
              <div className="mt-2 border-t border-cyber-border/60 pt-2">
                {replyFiles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {replyFiles.map((file, index) => (
                      <div
                        key={`${file.name}:${file.size}:${file.lastModified}`}
                        className="inline-flex items-center gap-2 rounded-full border border-cyber-border bg-cyber-bg/60 px-3 py-1 text-xs text-cyber-text"
                      >
                        <span className="max-w-[180px] truncate" title={file.name}>
                          {file.name}
                        </span>
                        <span className="text-cyber-muted">{formatAttachmentSize(file.size)}</span>
                        <button
                          type="button"
                          onClick={() => removeReplyFile(index)}
                          className="text-cyber-muted transition-all hover:text-cyber-red"
                          title="Remove"
                          aria-label={`Remove ${file.name}`}
                          disabled={isReplyBusy}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-cyber-muted">Drop files to attach</div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
